import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Checkbox, Collapse, Input, Modal, Select, Tag, Tooltip } from "antd";
import { InfoCircleOutlined, SaveOutlined, SearchOutlined } from "@ant-design/icons";
import { PremiumFeatureDisabled } from "@components/Common/PremiumFeatureDisabled";
import { AdminNotice } from "@components/Settings/AdminNotice";
import { getPermissionUsers, saveUserPermissions } from "@store/actions/team.actions";
import { isStaffEmail } from "@utils/user_utils";
import {
  changedKeys,
  changeSummary,
  groupPermissions,
  isActive,
  isGranted,
  matchesSearch,
  sectionSummary,
  splitOnMatches,
  toPermissionType,
  type Grants,
  type PermissionSection,
  type PermissionType,
  type PermissionTypeRow,
} from "@utils/platform_permissions";
import BlurredPlatformPermissions from "./blurred_platform_permissions.png";
import "@styles/styles-PlatformPermissions.css";

const MANAGER_KEY = "PermissionsManager";

type PermissionUser = {
  user_id: number;
  email: string;
  first_name: string;
  last_name: string;
  platform_permissions: Grants;
};

type OnGrant = (keys: string[], on: boolean) => void;

const highlight = (text: string, query: string) =>
  splitOnMatches(text, query).map((part, index) =>
    index % 2 ? (
      <mark key={index} className="platform-permissions__match">
        {part}
      </mark>
    ) : (
      part
    )
  );

type RowProps = { permission: PermissionType; query: string; granted: boolean; changed: boolean; onGrant: OnGrant };

const PermissionRow = ({ permission, query, granted, changed, onGrant }: RowProps) => {
  const id = `permission-${permission.key}`;
  return (
    <div className={`platform-permissions__row${changed ? " platform-permissions__row--changed" : ""}`}>
      <Checkbox id={id} checked={granted} onChange={(e) => onGrant([permission.key], e.target.checked)} />
      <label htmlFor={id} className="platform-permissions__label">
        {highlight(permission.label, query)}
      </label>
      <span className="platform-permissions__desc" title={permission.desc}>
        {highlight(permission.desc, query)}
      </span>
      <span className="platform-permissions__tags">
        {permission.warning && (
          <Tooltip title={permission.warning}>
            <Tag color="purple">Admin</Tag>
          </Tooltip>
        )}
        {changed && <span className="platform-permissions__unsaved">Unsaved</span>}
      </span>
    </div>
  );
};

type SectionsProps = {
  sections: PermissionSection[];
  hasHidden: boolean;
  draft: Grants;
  changed: Set<string>;
  onGrant: OnGrant;
};

const PermissionSections = ({ sections, hasHidden, draft, changed, onGrant }: SectionsProps) => {
  const [query, setQuery] = useState("");
  const [openSection, setOpenSection] = useState<string | null>(null);
  const searching = query.trim() !== "";

  const toItem = (section: PermissionSection) => {
    const groups = section.groups
      .map((group) => ({ ...group, rows: group.permissions.filter((permission) => matchesSearch(permission, query)) }))
      .filter((group) => group.rows.length);
    if (!groups.length) return [];
    const keys = section.groups.flatMap((group) => group.permissions.map((permission) => permission.key));
    const granted = keys.filter((key) => isGranted(draft, key)).length;
    const unsaved = keys.filter((key) => changed.has(key)).length;
    const summary = sectionSummary(section, draft);
    return [
      {
        key: section.name,
        label: (
          <span className="platform-permissions__section-head">
            <span className="platform-permissions__section-title">{highlight(section.name, query)}</span>
            <span className="platform-permissions__section-summary" title={summary}>
              {summary}
            </span>
            {unsaved > 0 && <span className="platform-permissions__unsaved">{unsaved} unsaved</span>}
          </span>
        ),
        extra: (
          <span className="platform-permissions__section-extra">
            <span className="platform-permissions__pill">
              {granted}/{keys.length}
            </span>
            <Checkbox
              checked={granted === keys.length}
              indeterminate={granted > 0 && granted < keys.length}
              onChange={(e) => onGrant(keys, e.target.checked)}
              aria-label={`Every ${section.name} permission`}
            />
          </span>
        ),
        children: (
          <div className="platform-permissions__rows">
            {groups.map((group) => {
              const groupKeys = group.permissions.map((permission) => permission.key);
              const grantedInGroup = groupKeys.filter((key) => isGranted(draft, key)).length;
              return (
                <div key={group.name} className="platform-permissions__group">
                  <div className="platform-permissions__group-head">
                    {groupKeys.length > 1 && (
                      <Checkbox
                        checked={grantedInGroup === groupKeys.length}
                        indeterminate={grantedInGroup > 0 && grantedInGroup < groupKeys.length}
                        onChange={(e) => onGrant(groupKeys, e.target.checked)}
                        aria-label={`Every ${group.name} permission`}
                      />
                    )}
                    <span className="platform-permissions__group-name">{highlight(group.name, query)}</span>
                  </div>
                  {group.rows.map((permission) => (
                    <PermissionRow
                      key={permission.key}
                      permission={permission}
                      query={query}
                      granted={isGranted(draft, permission.key)}
                      changed={changed.has(permission.key)}
                      onGrant={onGrant}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        ),
      },
    ];
  };

  const items = sections.flatMap(toItem);
  return (
    <>
      <Input
        allowClear
        prefix={<SearchOutlined />}
        placeholder="Search permissions"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search permissions"
      />
      {searching && <div className="platform-permissions__hint">A search opens every section with a match.</div>}
      {hasHidden && (
        <div className="platform-permissions__hidden">
          <InfoCircleOutlined />
          The list below only shows the permissions for active features.
        </div>
      )}
      {items.length > 0 && (
        <Collapse
          className="platform-permissions__sections"
          collapsible="header"
          accordion={!searching}
          activeKey={searching ? items.map((item) => item.key) : openSection ?? []}
          onChange={(keys) => {
            if (!searching) setOpenSection(keys[0] ?? null);
          }}
          items={items}
        />
      )}
      {searching && !items.length && (
        <div className="platform-permissions__empty">
          No permissions match this search.{" "}
          <Button size="small" onClick={() => setQuery("")}>
            Clear search
          </Button>
        </div>
      )}
    </>
  );
};

const PermissionEditor = ({ ownEmail }: { ownEmail: string | undefined }) => {
  const dispatch = useDispatch();
  const permissionRows: PermissionTypeRow[] | undefined = useSelector(({ team }: any) => team.misc?.permission_types);
  const teamInfo = useSelector(({ team }: any) => team.info);
  const users: PermissionUser[] = useSelector(({ team }: any) => team.permissionUsers);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Grants>({});
  const [saving, setSaving] = useState(false);

  const permissions = useMemo(() => (permissionRows ?? []).map(toPermissionType), [permissionRows]);
  const active = useMemo(() => permissions.filter((permission) => isActive(permission, teamInfo)), [permissions, teamInfo]);
  const sections = useMemo(() => groupPermissions(active), [active]);

  const selectedUser = users.find((u) => u.user_id === selectedUserId);
  const changed = changedKeys(active, selectedUser?.platform_permissions ?? {}, draft);
  const isSelf = !!selectedUser && selectedUser.email.toLowerCase() === ownEmail?.toLowerCase();
  const managerLabel = permissions.find((permission) => permission.key === MANAGER_KEY)?.label ?? MANAGER_KEY;
  const summary = changeSummary(changed, draft);

  const pickUser = (userId: number) => {
    const pick = () => {
      setSelectedUserId(userId);
      setDraft({ ...users.find((u) => u.user_id === userId)?.platform_permissions });
    };
    if (!changed.length) return pick();
    Modal.confirm({
      title: "Discard unsaved changes?",
      content: `You have unsaved changes for ${selectedUser?.first_name} ${selectedUser?.last_name}.`,
      okText: "Discard changes",
      cancelText: "Keep editing",
      onOk: pick,
    });
  };

  const grant: OnGrant = (keys, on) => {
    const apply = () => setDraft((prev) => ({ ...prev, ...Object.fromEntries(keys.map((key) => [key, on])) }));
    const removesOwnAccess = isSelf && !on && keys.includes(MANAGER_KEY) && isGranted(draft, MANAGER_KEY);
    if (!removesOwnAccess) return apply();
    Modal.confirm({
      title: "Remove your own access to this page?",
      content: `If you remove ${managerLabel}, you cannot open Platform Permissions again. Another permissions manager must give it back to you.`,
      okText: "Remove it",
      okButtonProps: { danger: true },
      cancelText: "Keep it",
      onOk: apply,
    });
  };

  const save = async () => {
    if (!selectedUser) return;
    setSaving(true);
    const changes = Object.fromEntries(changed.map((key) => [key, isGranted(draft, key)]));
    await dispatch(saveUserPermissions(selectedUser.user_id, changes) as any);
    setSaving(false);
  };

  return (
    <>
      <div className="platform-permissions__top">
        <Select
          className="platform-permissions__user-select"
          showSearch
          optionFilterProp="label"
          placeholder="Select a coach to manage permissions"
          value={selectedUserId ?? undefined}
          onChange={pickUser}
          options={users.map((u) => ({ value: u.user_id, label: `${u.first_name} ${u.last_name} (${u.email})` }))}
        />
        {selectedUser && (
          <span className="platform-permissions__save">
            <span className="platform-permissions__summary" title={summary}>
              {summary}
            </span>
            <Button type="primary" icon={<SaveOutlined />} disabled={!changed.length} loading={saving} onClick={save}>
              Save
            </Button>
          </span>
        )}
      </div>
      {isSelf && (
        <Alert
          type="warning"
          showIcon
          message={
            <>
              You are editing your own permissions. If you remove <strong>{managerLabel}</strong>, you cannot open this
              page again.
            </>
          }
        />
      )}
      {selectedUser && (
        <PermissionSections
          sections={sections}
          hasHidden={active.length < permissions.length}
          draft={draft}
          changed={new Set(changed)}
          onGrant={grant}
        />
      )}
    </>
  );
};

export const PlatformPermissions = ({ userPermissions }: { userPermissions: Grants | null | undefined }) => {
  const dispatch = useDispatch();
  const teamInfo = useSelector(({ team }: any) => team.info);
  const { user } = useAuth0();
  const isStaff = isStaffEmail(user?.email);
  const canManage = userPermissions?.[MANAGER_KEY] === true;

  useEffect(() => {
    if (canManage) dispatch(getPermissionUsers() as any);
  }, [canManage, dispatch]);

  if (!teamInfo.platform_permissions_enabled && !isStaff) {
    return (
      <PremiumFeatureDisabled
        bg_image={BlurredPlatformPermissions}
        show_navbar={false}
        feature_name={"Platform Permissions"}
        info_text={<>
        This is a premium feature that will allow you assign permissions to users on your team. <br></br>
        If you are interested in adding this to your platform, please contact your sales rep!</>}
      />
    );
  }

  if (!canManage) {
    return (
      <div>
        You do not have permissions to view this page. Only the team's Permissions Manager can view this page. <br/>
        If you want to add/remove specific permissions to your account, please reach out to your team's permissions manager. <br/><br/>
        Your team's Permissions Manager(s): <strong>{teamInfo.permissions_manager_list.map((manager: any) => `${manager.first_name} ${manager.last_name}`).join(", ")}</strong>
      </div>
    );
  }

  return (
    <div className="platform-permissions">
      {!teamInfo.platform_permissions_enabled && <AdminNotice />}
      <PermissionEditor ownEmail={user?.email} />
    </div>
  );
};
